if (!customElements.get('cart-target-remaining-discount')) {
  const FETCH_TIMEOUT_MS = 5000;

  class CartTargetRemainingDiscount extends HTMLElement {
    connectedCallback() {
      this._abort = new AbortController();

      this.targetShop = parseInt(this.dataset.targetShop, 10) || 0;
      this.cartTotal = parseInt(this.dataset.cartTotal, 10) || 0;
      this.country = this.dataset.country || '';
      this.currency = this.dataset.currency || '';
      this.variantId = this.dataset.variantId || '';

      if (!this.variantId || !this.country) {
        this.fail();
        return;
      }

      this.classList.add('is-converting');
      this.convert();
    }

    disconnectedCallback() {
      if (this._abort) this._abort.abort();
    }

    finish() {
      this.classList.remove('is-converting');
    }

    ready() {
      this.classList.remove('is-awaiting-rate');
      this.removeAttribute('aria-busy');
    }

    fail() {
      if (this.classList.contains('is-awaiting-rate')) {
        this.hidden = true;
      }
      this.finish();
    }

    async convert() {
      try {
        const rate = await this.getRate();

        if (!rate) {
          this.fail();
          return;
        }

        const convTarget = Math.round(this.targetShop * rate);
        const remaining = Math.max(0, convTarget - this.cartTotal);

        if (remaining <= 0) {
          this.ready();
          this.hidden = true;
          return;
        }

        let percentRemaining = convTarget > 0 ? (remaining * 100) / convTarget : 0;
        percentRemaining = Math.min(100, Math.max(0, percentRemaining));

        const remainingEl = this.querySelector('.js-target-remaining__remaining');
        const targetEl = this.querySelector('.js-target-remaining__target');
        const barEl = this.querySelector('.js-target-remaining__bar');

        if (remainingEl) {
          remainingEl.textContent = this.formatMoney(remaining);
        }
        if (targetEl) {
          targetEl.textContent = this.formatMoney(convTarget);
        }
        if (barEl) {
          barEl.style.width = 100 - percentRemaining + '%';
          barEl.dataset.remaining_price = remaining;
          barEl.dataset.percent = percentRemaining;
        }

        this.ready();
      } catch (err) {
        this.fail();
      } finally {
        this.finish();
      }
    }

    // Returns a positive numeric rate or null on any failure / shop currency.
    async getRate() {
      const params = new URLSearchParams({ variantId: this.variantId });
      if (this.country) params.set('country', this.country);

      // Reuse the existing controller so a timeout rejects the fetch through the
      // same AbortError path as an unmount, landing in convert()'s catch/finally.
      const timeoutId = setTimeout(() => this._abort.abort(), FETCH_TIMEOUT_MS);

      try {
        const response = await fetch(
          '/apps/spry-tool/currency-rate?' + params.toString(),
          {
            headers: { Accept: 'application/json' },
            signal: this._abort.signal,
          }
        );

        if (!response.ok) return null;

        const data = await response.json();
        if (!data || data.error) return null;

        const rate = parseFloat(data.rate);
        if (!(isFinite(rate) && rate > 0)) return null;

        return rate;
      } finally {
        clearTimeout(timeoutId);
      }
    }

    formatMoney(cents) {
      if (this.currency) {
        try {
          const locale = document.documentElement.lang || undefined;
          const amount = cents / 100;
          return new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: this.currency,
            currencyDisplay: 'narrowSymbol',
          }).format(amount);
        } catch (err) {
        }
      }

      const settings = (window.theme && window.theme.settings) || {};
      const format = settings.moneyFormat || settings.moneyWithCurrencyFormat;

      if (!format) {
        return this.formatWithDelimiters(cents, 2);
      }

      const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
      if (!match) return String(cents / 100);

      let value;
      switch (match[1]) {
        case 'amount':
          value = this.formatWithDelimiters(cents, 2);
          break;
        case 'amount_no_decimals':
          value = this.formatWithDelimiters(cents, 0);
          break;
        case 'amount_with_comma_separator':
          value = this.formatWithDelimiters(cents, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = this.formatWithDelimiters(cents, 0, '.', ',');
          break;
        default:
          value = this.formatWithDelimiters(cents, 2);
          break;
      }

      return format.replace(/\{\{\s*\w+\s*\}\}/, value);
    }

    formatWithDelimiters(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands == null ? ',' : thousands;
      decimal = decimal == null ? '.' : decimal;

      if (isNaN(number) || number == null) return '0';

      number = (number / 100.0).toFixed(precision);
      const parts = number.split('.');
      parts[0] = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return parts[0] + (parts[1] ? decimal + parts[1] : '');
    }
  }

  customElements.define('cart-target-remaining-discount', CartTargetRemainingDiscount);
}
